/**
 * DigestReport — on-demand Activity Digest under Reports.
 *
 * Same executive-summary format as the scheduled Daily Digest, but the user
 * picks any date range. Super Admins can also narrow to one company and export
 * to PDF / Excel. Clicking any tile or row opens a detail dialog listing the
 * contributing order forms (or raw-material packets) with their quantities.
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import AppShell from '@/components/AppShell';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { extractError } from '@/lib/errors';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { DigestView } from '@/components/digest/DigestView';
import { DigestDetailDialog } from '@/components/digest/DigestDetailDialog';
import { CalendarRange, Inbox, Download, ChevronDown, FileText, FileSpreadsheet, Building2, Loader2, RefreshCw, Clock } from 'lucide-react';
import { toast } from 'sonner';

const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

function presetRange(key) {
  const today = new Date();
  switch (key) {
    case 'today': return [isoOf(today), isoOf(today)];
    case 'yesterday': { const y = addDays(today, -1); return [isoOf(y), isoOf(y)]; }
    case 'last7': return [isoOf(addDays(today, -6)), isoOf(today)];
    case 'last30': return [isoOf(addDays(today, -29)), isoOf(today)];
    case 'thisMonth': return [isoOf(new Date(today.getFullYear(), today.getMonth(), 1)), isoOf(today)];
    case 'lastMonth': {
      const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const last = new Date(today.getFullYear(), today.getMonth(), 0);
      return [isoOf(first), isoOf(last)];
    }
    default: return [isoOf(today), isoOf(today)];
  }
}

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'lastMonth', label: 'Last month' },
];

// Activity Digest auto-refreshes every 5 minutes so the section stays live.
const AUTO_REFRESH_SEC = 300;
const fmtCountdown = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

export default function DigestReport() {
  const { isSuperAdmin } = useAuth();
  const [initial] = useState(() => presetRange('today'));
  const [start, setStart] = useState(initial[0]);
  const [end, setEnd] = useState(initial[1]);
  const [companyId, setCompanyId] = useState('all');
  const [companyOptions, setCompanyOptions] = useState([]);
  const [activePreset, setActivePreset] = useState('today');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [payload, setPayload] = useState(null);
  const [sparklines, setSparklines] = useState({});
  const [sparkWindow, setSparkWindow] = useState(null);

  // Drill-down dialog
  const [drill, setDrill] = useState({ open: false, params: null, label: '' });

  // Auto-refresh: countdown to the next silent refresh + last-refreshed marker.
  const [secondsLeft, setSecondsLeft] = useState(AUTO_REFRESH_SEC);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  // Adaptive momentum sparklines for the tiles — follow the selected range
  // (always end on the chosen "To" date, span >= 7 days, bucketed if long).
  const fetchSparklines = useCallback(async (data, coId, s, e) => {
    try {
      const params = {};
      if (s) params.start = s;
      if (e) params.end = e;
      if (coId && coId !== 'all') {
        params.company_id = coId;
      } else {
        const ids = (data?.companies || []).filter((c) => c.has_activity).map((c) => c.company_id).filter(Boolean);
        if (!ids.length) { setSparklines({}); setSparkWindow(null); return; }
        params.company_ids = ids.join(',');
      }
      const { data: sp } = await api.get('/reports/digest/sparklines', { params });
      setSparklines(sp?.companies || {});
      setSparkWindow(sp?.window || null);
    } catch {
      setSparklines({});
      setSparkWindow(null);
    }
  }, []);

  const fetchDigest = useCallback(async (s, e, coId, silent = false) => {
    if (!s || !e) return;
    if (!silent) setLoading(true);
    try {
      const params = { start: s, end: e };
      if (coId && coId !== 'all') params.company_id = coId;
      const { data } = await api.get('/reports/digest', { params });
      setPayload(data);
      if (Array.isArray(data.all_companies)) setCompanyOptions(data.all_companies);
      fetchSparklines(data, coId, s, e);
      setLastRefreshedAt(new Date());
    } catch (err) {
      if (!silent) {
        toast.error(extractError(err, 'Failed to load digest'));
        setPayload(null);
      }
    } finally {
      if (!silent) setLoading(false);
      // Any fetch (manual, preset, company change or auto) restarts the timer.
      setSecondsLeft(AUTO_REFRESH_SEC);
    }
  }, [fetchSparklines]);

  useEffect(() => { fetchDigest(initial[0], initial[1], 'all'); /* eslint-disable-next-line */ }, []);

  // Latest selection + gating flags for the ticking interval (avoids stale closures).
  const liveRef = useRef({ start, end, companyId, refreshing, drillOpen: drill.open });
  useEffect(() => {
    liveRef.current = { start, end, companyId, refreshing, drillOpen: drill.open };
  }, [start, end, companyId, refreshing, drill.open]);

  const refreshNow = useCallback(async () => {
    if (liveRef.current.refreshing) return;
    setRefreshing(true);
    const { start: s, end: e, companyId: c } = liveRef.current;
    await fetchDigest(s, e, c, true);
    setRefreshing(false);
  }, [fetchDigest]);

  // Tick once a second; pause while the tab is hidden, a drill dialog is open,
  // or a refresh is already in flight.
  useEffect(() => {
    const id = setInterval(() => {
      const { refreshing: busy, drillOpen } = liveRef.current;
      if (document.visibilityState !== 'visible' || busy || drillOpen) return;
      setSecondsLeft((s) => (s <= 1 ? 0 : s - 1));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  // When the countdown reaches zero, silently refresh the current selection.
  useEffect(() => {
    if (secondsLeft === 0 && !liveRef.current.refreshing) refreshNow();
  }, [secondsLeft, refreshNow]);

  const applyPreset = (key) => {
    const [s, e] = presetRange(key);
    setStart(s); setEnd(e); setActivePreset(key);
    fetchDigest(s, e, companyId);
  };
  const applyCustom = () => { setActivePreset(null); fetchDigest(start, end, companyId); };
  const onCompanyChange = (v) => { setCompanyId(v); fetchDigest(start, end, v); };

  const doExport = async (fmt) => {
    setExporting(true);
    try {
      const params = { start, end };
      if (isSuperAdmin && companyId !== 'all') params.company_id = companyId;
      const res = await api.get(`/reports/digest/${fmt}`, { params, responseType: 'blob' });
      const type = fmt === 'pdf'
        ? 'application/pdf'
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      const url = window.URL.createObjectURL(new Blob([res.data], { type }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity_digest_${start}_${end}.${fmt === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Digest ${fmt.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(extractError(err, `Failed to export ${fmt.toUpperCase()}`));
    } finally {
      setExporting(false);
    }
  };

  const subjectLabel = (key) => (payload?.subjects || []).find((s) => s.key === key)?.label || 'Detail';
  const onDrill = (subjectKey, company) => {
    setDrill({
      open: true,
      label: subjectLabel(subjectKey),
      params: { subject: subjectKey, company_id: company?.company_id, start, end },
    });
  };

  const noSubjects = payload && (!payload.subjects || payload.subjects.length === 0);

  return (
    <AppShell title="Activity Digest" subtitle="View your activity digest for any date range — same format as the daily digest.">
      <div className="space-y-4" data-testid="digest-report-page">
        {/* Range controls */}
        <Card className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-end gap-3">
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">From</label>
                <DatePicker value={start} onValueChange={setStart} maxDate={end || undefined} className="w-[150px]" data-testid="digest-report-start" />
              </div>
              <div>
                <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">To</label>
                <DatePicker value={end} onValueChange={setEnd} minDate={start || undefined} className="w-[150px]" data-testid="digest-report-end" />
              </div>
              {isSuperAdmin && (
                <div>
                  <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Company</label>
                  <Select value={companyId} onValueChange={onCompanyChange}>
                    <SelectTrigger className="h-9 w-[220px]" data-testid="digest-report-company">
                      <SelectValue placeholder="All Companies" />
                    </SelectTrigger>
                    <SelectContent className="max-h-[320px]">
                      <SelectItem value="all">All Companies</SelectItem>
                      {companyOptions.map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button className="h-9 bg-[var(--ink-navy)] hover:bg-[var(--ink-navy-2)] text-white" onClick={applyCustom} data-testid="digest-report-apply">
                <CalendarRange size={14} className="mr-1.5" /> View
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="h-9" disabled={exporting || loading || noSubjects} data-testid="digest-report-export">
                    {exporting ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Download size={14} className="mr-1.5" />}
                    Export <ChevronDown size={13} className="ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => doExport('pdf')} data-testid="digest-export-pdf">
                    <FileText size={14} className="mr-2 text-rose-600" /> Download PDF
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => doExport('excel')} data-testid="digest-export-excel">
                    <FileSpreadsheet size={14} className="mr-2 text-emerald-600" /> Download Excel
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            <div className="flex flex-wrap gap-1.5 lg:ml-auto">
              {PRESETS.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => applyPreset(p.key)}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${activePreset === p.key ? 'border-[var(--ink-navy)] bg-[var(--ink-navy)] text-white' : 'border-[var(--divider)] bg-white text-slate-600 hover:bg-slate-50'}`}
                  data-testid={`digest-report-preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <div className="mt-2 text-[11px] text-slate-400 flex items-center gap-1.5">
            <Building2 size={12} /> Tip: click any tile or row to see the order forms / packets behind it.
          </div>
        </Card>

        {/* Result */}
        <Card className="p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2 border-b border-[var(--divider)] pb-3" data-testid="digest-autorefresh-bar">
            <div className="flex items-center gap-2 text-[13px] font-semibold text-[var(--ink-navy)]">
              <span className="relative inline-flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--gold-accent)] opacity-60 animate-ping" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--gold-accent)]" />
              </span>
              Activity Digest
            </div>
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-[11.5px] text-slate-500 tabular-nums" data-testid="digest-autorefresh-timer">
                <Clock size={12} className="text-slate-400" />
                {refreshing
                  ? 'Refreshing…'
                  : (<>Auto-refresh in <span className="font-mono font-medium text-[var(--ink-navy)]">{fmtCountdown(secondsLeft)}</span></>)}
              </span>
              {lastRefreshedAt && !refreshing && (
                <span className="hidden sm:inline text-[11px] text-slate-400" data-testid="digest-last-refreshed">
                  Updated {lastRefreshedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                className="h-8"
                onClick={refreshNow}
                disabled={refreshing || loading}
                data-testid="digest-refresh-now"
              >
                <RefreshCw size={13} className={`mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
          {loading ? (
            <div className="space-y-3" data-testid="digest-report-loading">
              <Skeleton className="h-6 w-64" />
              <div className="grid grid-cols-4 md:grid-cols-9 gap-2">
                {Array.from({ length: 9 }).map((_, i) => <Skeleton key={i} className="h-16" />)}
              </div>
              <Skeleton className="h-40 w-full" />
            </div>
          ) : noSubjects ? (
            <div className="flex flex-col items-center justify-center py-12 text-center" data-testid="digest-report-no-subjects">
              <div className="h-12 w-12 rounded-full bg-[var(--paper-warm)] border border-[var(--divider)] flex items-center justify-center mb-3">
                <Inbox size={22} className="text-slate-400" />
              </div>
              <div className="text-sm font-semibold text-[var(--ink-navy)]">No digest activities enabled for you</div>
              <div className="text-xs text-slate-500 mt-1 max-w-sm">
                Ask your administrator to subscribe you in <span className="font-medium">Daily Digest</span> settings to see your activity summary here.
              </div>
            </div>
          ) : (
            <DigestView payload={payload} titlePrefix="Activity Digest" onDrill={onDrill} sparklines={sparklines} sparkWindow={sparkWindow} />
          )}
        </Card>
      </div>

      <DigestDetailDialog
        open={drill.open}
        onOpenChange={(v) => setDrill((d) => ({ ...d, open: v }))}
        params={drill.params}
        label={drill.label}
      />
    </AppShell>
  );
}
